import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import { useInfoHelpModalStyles } from './style';

/**
 * Optional field-level help modal (`isModelTrue` + `modalConfig` on data elements).
 * Closes on backdrop only (no footer / no Escape). See SummaryBlock config docs.
 */

export function resolveInfoModalRowKeys(modalConfig) {
    const { columns, rows, rowKeys } = modalConfig || {};
    if (Array.isArray(rowKeys) && rowKeys.length) return rowKeys;
    if (rows?.[0] && typeof rows[0] === 'object' && !Array.isArray(rows[0])) {
        return Object.keys(rows[0]);
    }
    return (columns || []).map((_, i) => `col${i}`);
}

export function cellValueToTooltipString(raw) {
    if (raw == null) return '';
    if (Array.isArray(raw)) return raw.map((x) => `• ${String(x)}`).join('\n');
    return String(raw);
}

function HeaderCellWithTooltip({ label, classes }) {
    const title = label != null ? String(label) : '';
    const inner = <span className={classes.ellipsisOneLine}>{title}</span>;
    return (
        <TableCell component="th" scope="col" className={classes.tableHeadCell}>
            {title ? (
                <Tooltip
                    title={title}
                    placement="top"
                    enterDelay={400}
                    classes={{ popper: classes.tooltipPopper }}
                >
                    {inner}
                </Tooltip>
            ) : (
                inner
            )}
        </TableCell>
    );
}

function BodyCellWithTooltip({ raw, classes }) {
    const tip = cellValueToTooltipString(raw);

    if (raw != null && Array.isArray(raw)) {
        const list = (
            <ul className={classes.cellBulletList}>
                {raw.map((line, i) => (
                    <li key={i}>{String(line)}</li>
                ))}
            </ul>
        );
        return (
            <TableCell className={classes.tableBodyCell}>
                {tip ? (
                    <Tooltip
                        title={tip}
                        placement="top"
                        enterDelay={400}
                        classes={{ popper: classes.tooltipPopper }}
                    >
                        <span>{list}</span>
                    </Tooltip>
                ) : (
                    list
                )}
            </TableCell>
        );
    }

    const text = raw == null ? '' : String(raw);
    const inner = <span className={classes.ellipsisOneLine}>{text || '\u00a0'}</span>;
    return (
        <TableCell className={classes.tableBodyCell}>
            {tip ? (
                <Tooltip
                    title={tip}
                    placement="top"
                    enterDelay={400}
                    classes={{ popper: classes.tooltipPopper }}
                >
                    {inner}
                </Tooltip>
            ) : (
                inner
            )}
        </TableCell>
    );
}

/**
 * @param {boolean} open
 * @param {(event: object, reason: string) => void} onClose — parent should only close for `backdropClick`
 * @param {() => void} [onExited] — after close transition; parent should clear stored element here (avoids empty white paper during exit)
 * @param {object | null} modalConfig
 * @param {string} [label] — title fallback
 */
const InfoHelpModal = ({ open, onClose, onExited, modalConfig, label }) => {
    const classes = useInfoHelpModalStyles();

    const paperStyle = (() => {
        const cfg = modalConfig;
        if (!cfg) return { maxWidth: '95vw', maxHeight: '90vh' };
        return {
            width: cfg.modalWidth || 'min(90vw, 800px)',
            height: cfg.modalHeight != null ? cfg.modalHeight : 'auto',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'hidden',
        };
    })();

    const titleText = modalConfig?.title ?? label;

    const renderBody = () => {
        const cfg = modalConfig;
        const isText =
            cfg?.type === 'text' ||
            (cfg && (cfg.body != null || cfg.text != null) && !cfg.rows?.length);

        if (isText) {
            return (
                <Typography component="div" className={classes.textBody}>
                    {cfg?.body ?? cfg?.text ?? ''}
                </Typography>
            );
        }

        const columns = cfg?.columns || [];
        const rows = cfg?.rows || [];

        return (
            <div className={classes.tableSurface}>
                <Table size="small" className={classes.tableRoot}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col, ci) => (
                                <HeaderCellWithTooltip key={`h-${ci}-${String(col)}`} label={col} classes={classes} />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, ri) => {
                            const keys = resolveInfoModalRowKeys(cfg);
                            return (
                                <TableRow key={ri}>
                                    {columns.map((_, ci) => {
                                        const k = keys[ci];
                                        const cell = k != null ? row[k] : undefined;
                                        return (
                                            <BodyCellWithTooltip
                                                key={ci}
                                                raw={cell}
                                                classes={classes}
                                            />
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            disableEscapeKeyDown
            maxWidth={false}
            scroll="paper"
            TransitionProps={{ onExited }}
            BackdropProps={{
                className: classes.backdrop,
            }}
            PaperProps={{
                className: classes.dialogPaper,
                style: paperStyle,
            }}
        >
            {titleText ? (
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <div className={classes.dialogTitleInner}>
                        <InfoOutlined className={classes.titleIcon} />
                        <span className={classes.titleText}>{titleText}</span>
                    </div>
                </DialogTitle>
            ) : null}
            <DialogContent className={classes.dialogContent}>{renderBody()}</DialogContent>
        </Dialog>
    );
};

export default InfoHelpModal;
